import DOMPurify from 'dompurify';

const OUTBOUND_LINK_REL = 'noopener noreferrer nofollow';
const OPENS_IN_NEW_TAB = '(opens in a new tab)';

const applyOutboundLinkAttrs = (anchor: Element) => {
  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('rel', OUTBOUND_LINK_REL);

  const existingLabel = anchor.getAttribute('aria-label')?.trim();
  const linkText = (anchor.textContent ?? '').trim();
  const href = anchor.getAttribute('href')?.trim();
  const base = existingLabel || linkText || href || '';
  if (base && !/\(opens in a new (tab|window)\)/i.test(base)) {
    anchor.setAttribute('aria-label', `${base} ${OPENS_IN_NEW_TAB}`);
  }
};

/**
 * ### sanitizeHtmlString
 *
 * @description
 * Sanitizes untrusted HTML for display. Remaining anchors open in a new tab
 * with `rel="noopener noreferrer nofollow"` and an accessible name that
 * includes "(opens in a new tab)".
 *
 * Uses DOMPurify's `RETURN_DOM` so link hardening runs on the sanitized tree
 * rather than re-parsing via the global `document`. This helper is DOM-only:
 * when DOMPurify has no window (Node SSR), it returns `''` instead of
 * unsanitized markup.
 *
 * @example
 * sanitizeHtmlString('<a href="https://example.com">docs</a>');
 *
 * @param htmlString - Untrusted HTML, typically customer-authored rich text
 * @returns Sanitized HTML safe to assign to `dangerouslySetInnerHTML`
 */
export const sanitizeHtmlString = (htmlString: string): string => {
  if (typeof DOMPurify.sanitize !== 'function' || !DOMPurify.isSupported) {
    return '';
  }

  const sanitized = DOMPurify.sanitize(htmlString, { RETURN_DOM: true });
  if (typeof Element === 'undefined' || !(sanitized instanceof Element)) {
    return '';
  }

  for (const anchor of sanitized.querySelectorAll('a')) {
    applyOutboundLinkAttrs(anchor);
  }
  return sanitized.innerHTML;
};
